import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, Check, Loader2, AlertTriangle, Plus, ExternalLink } from 'lucide-react';
import { signupFormsApi, newslettersApi, formConfigsApi, emailTemplatesApi, extractLookupId } from '../services/creatorApi';
import { toast } from 'sonner';
/** Escape a string for safe embedding inside single-quoted JS strings */
function escapeForJSString(str: string): string {
  return str.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/<\/script>/gi, '<\\/script>');
}
interface EmbedParams {
  team: string;
  newsletterId: string;
  emailHtml: string;
  emailSubject: string;
  redirectUrl: string;
  region: string;
  appOrigin: string;
  successMessage: string;
  submittingText: string;
  errorMessage: string;
  validationErrorMessage: string;
  confirmationLanguage?: string;
  routingRules?: Array<{
    field: string;
    operator: 'equals' | 'not_equals' | 'contains';
    value: string;
    emailHtml: string;
    emailSubject: string;
  }>;
}
/** Generate a compact submission handler script with full double opt-in support */
function generateSubmissionScript(params: EmbedParams): string {
  console.log('[generateSubmissionScript][EmbedCodes]', {
    team: params.team,
    newsletterId: params.newsletterId,
    hasEmailHtml: !!params.emailHtml,
    emailHtmlLength: params.emailHtml?.length || 0,
    emailSubject: params.emailSubject,
    routingRulesCount: params.routingRules?.length,
    caller: new Error().stack?.split('\n')[2]?.trim()
  });
  const t = escapeForJSString(params.team);
  const nid = escapeForJSString(params.newsletterId);
  const reg = escapeForJSString(params.region);
  const origin = escapeForJSString(params.appOrigin);
  const redir = escapeForJSString(params.redirectUrl);
  const successMsg = escapeForJSString(params.successMessage || 'Successfully submitted! Please check your email to confirm.');
  const submittingText = escapeForJSString(params.submittingText || 'Enviando…');
  const errorMsg = escapeForJSString(params.errorMessage || 'Error al enviar. Inténtalo de nuevo.');
  const validationMsg = escapeForJSString(params.validationErrorMessage || 'Por favor, complete todos los campos obligatorios.');
  const redirBase = escapeForJSString(params.redirectUrl.includes('?') ? params.redirectUrl.substring(0, params.redirectUrl.indexOf('?')) : params.redirectUrl);
  const emailHtmlJson = JSON.stringify(params.emailHtml);
  const emailSubjectJson = JSON.stringify(params.emailSubject);
  // Serialize routing rules if present
  const hasRules = params.routingRules && params.routingRules.length > 0;
  const rulesJson = hasRules ? JSON.stringify(params.routingRules!.map(r => ({
    field: r.field,
    op: r.operator,
    val: r.value,
    html: r.emailHtml,
    subject: r.emailSubject
  }))) : '[]';
  const reportUrl = 'https://www.zohoapis.com/creator/v2.1/data/52studios/campaigns/report/All_Submissions';
  const formUrl = 'https://www.zohoapis.com/creator/v2.1/data/52studios/campaigns/form/Submissions';
  const proxyUrl = 'https://api.thechosen.tv/chosen_zoho_api.php';
  const apiKey = 'ud7^3jf(80';
  const langParam = params.confirmationLanguage ? `&lang=${escapeForJSString(params.confirmationLanguage)}` : '';
  return `(function(){
var f=document.querySelector('form');if(!f)return;
function showToast(msg,isErr){var t=document.getElementById('_ft52');if(t)t.remove();var s=document.createElement('style');s.textContent='@keyframes _fti{from{transform:translate(-50%,-16px);opacity:0}to{transform:translate(-50%,0);opacity:1}}@keyframes _fto{from{opacity:1}to{opacity:0;transform:translate(-50%,-16px)}}';document.head.appendChild(s);t=document.createElement('div');t.id='_ft52';t.style.cssText='position:fixed;top:24px;left:50%;transform:translateX(-50%);z-index:2147483647;background:'+(isErr?'#dc2626':'#16a34a')+';color:#fff;padding:14px 18px;border-radius:10px;font-size:14px;font-weight:500;box-shadow:0 8px 24px rgba(0,0,0,0.18);max-width:380px;display:flex;align-items:center;gap:12px;font-family:-apple-system,BlinkMacSystemFont,sans-serif;animation:_fti 0.3s ease';var sp=document.createElement('span');sp.textContent=msg;var cl=document.createElement('button');cl.textContent='\\u00d7';cl.style.cssText='background:none;border:none;color:rgba(255,255,255,0.8);font-size:20px;cursor:pointer;padding:0;line-height:1;margin-left:4px;flex-shrink:0';function dismiss(){t.style.animation='_fto 0.3s ease forwards';setTimeout(function(){if(t&&t.parentNode)t.remove()},300)}cl.onclick=dismiss;t.appendChild(sp);t.appendChild(cl);document.body.appendChild(t);setTimeout(dismiss,5000)}
function validateForm(){var rq=f.querySelectorAll('[required]');for(var i=0;i<rq.length;i++){var el=rq[i];if(el.type==='checkbox'){if(!el.checked)return false}else if(el.type==='radio'){var g=f.querySelectorAll('input[name="'+el.name+'"]');var ok=false;for(var j=0;j<g.length;j++){if(g[j].checked){ok=true;break}}if(!ok)return false}else{if(!el.value||!String(el.value).trim())return false}}return true}
function genToken(){var t=Date.now().toString(36),r='';for(var i=0;i<16;i++)r+=Math.floor(Math.random()*36).toString(36);return t+'_'+r}
function renderTpl(tpl,vars){var out=tpl;for(var k in vars){out=out.replace(new RegExp('\\\\{\\\\{\\\\s*'+k+'\\\\s*\\\\}}','gi'),vars[k]||'')}return out}
function escRe(s){return s.replace(/[.*+?^$()|[\\]\\\\]/g,'\\\\$&')}
function injectConfirmUrl(html,confirmUrl,redirBase){
  if(!redirBase)return html;
  var re=new RegExp('href=["\\']+'+escRe(redirBase)+'[^"\\']*["\\']+','gi');
  return html.replace(re,'href="'+confirmUrl+'"');
}
function callProxy(url,method,data,cb){
  fetch('${proxyUrl}',{method:'POST',headers:{'Content-Type':'application/json','API-KEY':'${apiKey}'},
  body:JSON.stringify({url:url,method:method,data:data})}).then(function(r){return r.json()}).then(cb).catch(function(e){cb(null,e)})
}
var _defaultHtml=${emailHtmlJson};
var _defaultSubject=${emailSubjectJson};
var _rules=${rulesJson};
f.addEventListener('submit',function(e){
e.preventDefault();e.stopImmediatePropagation();
if(!validateForm()){showToast('${validationMsg}',true);return;}
var d=new FormData(f),p={},b=f.querySelector('[type="submit"],button:not([type])'),o=b?b.textContent:'';
d.forEach(function(v,k){p[k]=v});
if(b){b.disabled=true;b.textContent='${submittingText}'}
var em=p.email||p.Email||p.email_address||p.EMAIL||'';
var tok=genToken();
var baseConfirmUrl='${origin}/confirm?token='+encodeURIComponent(tok)+'${langParam}';
var tplHtml=_defaultHtml,tplSubject=_defaultSubject;
for(var ri=0;ri<_rules.length;ri++){var _r=_rules[ri],_fv=(p[_r.field]||'').trim().toLowerCase(),_rv=(_r.val||'').trim().toLowerCase(),_m=false;if(_r.op==='equals')_m=(_fv===_rv);else if(_r.op==='not_equals')_m=(_fv!==_rv);else if(_r.op==='contains')_m=(_fv.indexOf(_rv)!==-1);if(_m&&_r.html){tplHtml=_r.html;tplSubject=_r.subject;break;}}
var baseVars=Object.assign({},p,{confirmation_link:baseConfirmUrl,team_name:'${t}'});
var renderedHtml=renderTpl(tplHtml,baseVars);
renderedHtml=injectConfirmUrl(renderedHtml,baseConfirmUrl,'${redirBase}');
var renderedSubject=renderTpl(tplSubject,baseVars);
var cc=false;var cis=f.querySelectorAll('input[type="checkbox"]');for(var ci=0;ci<cis.length;ci++){if((cis[ci].name||'').toLowerCase().indexOf('consent')!==-1&&cis[ci].checked){cc=true;break;}}
var submitStatus=${!params.emailHtml && (!params.routingRules || params.routingRules.length === 0) ? "'Confirmed'" : "'Not Subscribed'"};
var payload={Form_Payload:JSON.stringify(p),Status:submitStatus,Team:'${t}',Region:'${reg}',Newsletter_ID:'${nid}',Confirmation_Token:tok,Subscriber_Email:em,Rendered_Email_HTML:renderedHtml,Rendered_Email_Subject:renderedSubject};
if('${redir}')payload.Redirect_URL='${redir}';
callProxy('${formUrl}','POST',{data:payload},function(res){
  if(!res){showToast('${errorMsg}',true);if(b){b.disabled=false;b.textContent=o}return}
  var recordId=res&&res.data&&res.data.ID?String(res.data.ID):'';
  if(recordId){
    var fullConfirmUrl='${origin}/confirm?token='+encodeURIComponent(tok)+'&id='+encodeURIComponent(recordId)+'${langParam}';
    var updVars=Object.assign({},p,{confirmation_link:fullConfirmUrl,team_name:'${t}'});
    var updHtml=renderTpl(tplHtml,updVars);
    updHtml=injectConfirmUrl(updHtml,fullConfirmUrl,'${redirBase}');
    var updSubject=renderTpl(tplSubject,updVars);
    callProxy('${reportUrl}/'+recordId,'PATCH',{data:{Rendered_Email_HTML:updHtml,Rendered_Email_Subject:updSubject}},function(){})
  }
  showToast('${successMsg}',false);
  if(b){b.disabled=false;b.textContent=o}f.reset()
})
},true)
})();`;
}
/** Generate a compact JS embed snippet */
function generateJsEmbed(slug: string, html: string, css: string, js: string, embedParams: EmbedParams): string {
  if (!html.trim()) return '';
  var id = 'form-52s-' + (slug || 'preview');
  var safeHtml = escapeForJSString(html);
  var safeCss = escapeForJSString(css);
  var handler = generateSubmissionScript(embedParams);
  var safeHandler = handler.replace(/<\/script>/gi, '<\\/script>');
  var safeJs = js ? js.replace(/<\/script>/gi, '<\\/script>') : '';
  var out = `<div id="${id}"></div>\n<script>\n(function(){var c=document.getElementById('${id}');if(!c)return;c.innerHTML='<sty` + 'le>' + safeCss + '</sty' + 'le>' + safeHtml + `';var s=document.createElement('script');s.textContent=${JSON.stringify(safeHandler)};c.appendChild(s);`;
  if (safeJs.trim()) {
    out += `var u=document.createElement('script');u.textContent=${JSON.stringify(safeJs)};c.appendChild(u);`;
  }
  out += `})();\n<\/script>`;
  return out;
}
/** Generate a compact iframe embed using srcdoc */
function generateIframeEmbed(slug: string, html: string, css: string, js: string, embedParams: EmbedParams): string {
  if (!html.trim()) return '';
  var reset = '*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;padding:1rem}';
  var allCss = reset + (css || '');
  var handler = generateSubmissionScript(embedParams);
  var doc = '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><sty' + 'le>' + allCss + '</sty' + 'le></head><body>' + html + '<script>' + handler + '<\/script>';
  if (js && js.trim()) {
    doc += '<script>' + js + '<\/script>';
  }
  doc += '</body></html>';
  var srcdoc = doc.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return '<iframe srcdoc="' + srcdoc + '" width="100%" height="500" frameborder="0" style="border:none;overflow:hidden" allowtransparency="true"></iframe>';
}
export function EmbedCodes() {
  const {
    id
  } = useParams();
  const [copied, setCopied] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [formName, setFormName] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [scriptCode, setScriptCode] = useState('');
  const [iframeCode, setIframeCode] = useState('');
  const [wpCode, setWpCode] = useState('');
  const [hasNewsletter, setHasNewsletter] = useState(true);
  const [missingConfirmPlaceholder, setMissingConfirmPlaceholder] = useState(false);
  const [activeInstructionTab, setActiveInstructionTab] = useState<'wordpress' | 'html' | 'react'>('wordpress');
  const iframePreRef = useRef<HTMLPreElement>(null);
  const scriptPreRef = useRef<HTMLPreElement>(null);
  const wpPreRef = useRef<HTMLPreElement>(null);
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const scriptContainerRef = useRef<HTMLDivElement>(null);
  const wpContainerRef = useRef<HTMLDivElement>(null);
  // Block manual copy (Ctrl+C / Cmd+C) on code blocks when no newsletter
  useEffect(() => {
    if (hasNewsletter) return;
    const blockCopy = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement;
      if (iframeContainerRef.current?.contains(target) || scriptContainerRef.current?.contains(target)) {
        e.preventDefault();
        toast.error('Embed code copying is disabled', {
          description: 'Create a Newsletter for this form first to enable embed codes.',
          duration: 5000
        });
      }
    };
    document.addEventListener('copy', blockCopy);
    return () => document.removeEventListener('copy', blockCopy);
  }, [hasNewsletter]);
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    signupFormsApi.get(id).then(async record => {
      if (record) {
        setFormName(record.Form_Name || '');
        var slug = record.Url_Slug || '';
        setFormSlug(slug);
        var html = record.HTML || '';
        var css = record.CSS || '';
        var js = record.JavaScript1 || '';
        var team = record.Team || '';
        var region = record.Region || '';
        const embedParams: EmbedParams = {
          team,
          region,
          newsletterId: '',
          emailHtml: '',
          emailSubject: '',
          redirectUrl: '',
          appOrigin: window.location.origin,
          successMessage: 'Successfully submitted! Please check your email to confirm.',
          submittingText: 'Enviando…',
          errorMessage: 'Error al enviar. Inténtalo de nuevo.',
          validationErrorMessage: 'Por favor, complete todos los campos obligatorios.',
          routingRules: []
        };
        // Extract routing rules from Builder_Schema
        let emailRoutingRules: any[] = [];
        if ((record as any).Builder_Schema) {
          try {
            const schema = JSON.parse((record as any).Builder_Schema);
            if (schema.successMessage) {
              embedParams.successMessage = schema.successMessage;
            }
            if (schema.submittingText) {
              embedParams.submittingText = schema.submittingText;
            }
            if (schema.errorMessage) {
              embedParams.errorMessage = schema.errorMessage;
            }
            if (schema.validationErrorMessage) {
              embedParams.validationErrorMessage = schema.validationErrorMessage;
            }
            if (schema.confirmationLanguage) {
              embedParams.confirmationLanguage = schema.confirmationLanguage;
            }
            if (schema.emailRoutingRules && Array.isArray(schema.emailRoutingRules)) {
              emailRoutingRules = schema.emailRoutingRules;
            }
          } catch {
            /* ignore */
          }
        }
        try {
          const allNewsletters = await newslettersApi.list();
          const linkedNewsletter = allNewsletters.find(nl => {
            const nlFormId = extractLookupId(nl.Sign_Up_Form);
            return nlFormId === id;
          });
          if (linkedNewsletter) {
            setHasNewsletter(true);
            embedParams.newsletterId = linkedNewsletter.Newsletter_ID || '';
            try {
              const configs = await formConfigsApi.findByFormId(id);
              if (configs.length > 0) {
                const templateId = extractLookupId(configs[0].Email_Template);
                if (templateId) {
                  const template = await emailTemplatesApi.get(templateId);
                  if (template) {
                    embedParams.emailHtml = template.Email_HTML_Body || '';
                    embedParams.emailSubject = template.Email_Subject || '';
                    const htmlBody = template.Email_HTML_Body || '';
                    const hasPlaceholder = /\{\{\s*confirmation_link\s*\}\}/i.test(htmlBody);
                    setMissingConfirmPlaceholder(!hasPlaceholder);
                    const rawLink = (template as any).Confirmation_Link;
                    if (rawLink) {
                      embedParams.redirectUrl = typeof rawLink === 'string' ? rawLink : rawLink?.url || '';
                    }
                  }
                }
              }
              // Fetch templates for conditional routing rules
              if (emailRoutingRules.length > 0) {
                const resolvedRules: EmbedParams['routingRules'] = [];
                for (const rule of emailRoutingRules) {
                  if (rule.templateId && rule.field && rule.value) {
                    try {
                      const ruleTemplate = await emailTemplatesApi.get(rule.templateId);
                      if (ruleTemplate) {
                        resolvedRules.push({
                          field: rule.field,
                          operator: rule.operator || 'equals',
                          value: rule.value,
                          emailHtml: ruleTemplate.Email_HTML_Body || '',
                          emailSubject: ruleTemplate.Email_Subject || ''
                        });
                      }
                    } catch (err) {
                      console.warn(`Failed to fetch template for routing rule (field: ${rule.field}):`, err);
                    }
                  }
                }
                embedParams.routingRules = resolvedRules;
              }
            } catch (err) {
              console.warn('Could not load form config/template:', err);
            }
          } else {
            setHasNewsletter(false);
          }
        } catch {
          setHasNewsletter(false);
        }
        setScriptCode(generateJsEmbed(slug, html, css, js, embedParams));
        setIframeCode(generateIframeEmbed(slug, html, css, js, embedParams));
        setWpCode(`<iframe src="${window.location.origin}/embed/${slug}" width="100%" height="600" frameborder="0" style="border:none;overflow:hidden;width:100%;" allowtransparency="true" referrerpolicy="unsafe-url"></iframe>`);
      } else {
        toast.error('Form not found');
      }
    }).catch(err => {
      toast.error('Failed to load form data');
      console.error(err);
    }).finally(() => setLoading(false));
  }, [id]);
  const handleCopy = async (type: string, text: string, preRef: React.RefObject<HTMLPreElement>) => {
    if (!hasNewsletter) {
      toast.error('Cannot copy embed code', {
        description: 'You must create a Newsletter for this form before you can use embed codes.',
        duration: 6000,
        action: {
          label: 'Create Newsletter',
          onClick: () => {
            window.location.href = '/newsletters/new';
          }
        }
      });
      return;
    }
    let copySucceeded = false;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(null), 2000);
      copySucceeded = true;
    } catch {}
    if (!copySucceeded) {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0';
        document.body.appendChild(textarea);
        textarea.select();
        textarea.setSelectionRange(0, text.length);
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (ok) {
          setCopied(type);
          toast.success('Copied to clipboard');
          setTimeout(() => setCopied(null), 2000);
          copySucceeded = true;
        }
      } catch {}
    }
    if (!copySucceeded) {
      if (preRef.current) {
        const range = document.createRange();
        range.selectNodeContents(preRef.current);
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
        }
        const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
        toast.info(`Text selected — press ${isMac ? '⌘C' : 'Ctrl+C'} to copy`, {
          duration: 4000
        });
      } else {
        toast.error('Failed to copy — try selecting the code manually');
      }
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center py-24" data-id="element-4179" data-mp="156q8glt2g6g6">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-4180" data-mp="1a3grni063i5t" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-4181" data-mp="1k86wpwqvb2e9">
          Loading embed codes...
        </span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-4182" data-mp="1f8u9vbzzxvbo">
      <div className="flex items-center gap-4" data-id="element-4183" data-mp="1f7ixwowedh9h">
        <Link to="/forms" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-4184" data-mp="1a9r2hkwjnd0j">
          <ArrowLeft className="h-5 w-5" data-id="element-4185" data-mp="1697aevqtuzl9" />
        </Link>
        <div className="flex-1" data-id="element-4186" data-mp="16as4kk98cgil">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4187" data-mp="22z7w2lvwgzyc">Embed Codes</h1>
          <p className="text-sm text-gray-500" data-id="element-4188" data-mp="1xzljmenwbnql">
            {formName ? 'Embed codes for "' + formName + '"' : 'Choose an integration method for your website.'}
          </p>
        </div>
        {formSlug && <a href={`/embed/${formSlug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors shadow-sm" data-id="element-4189" data-mp="1p6i666d93avy">
            <ExternalLink className="h-4 w-4" data-id="element-4190" data-mp="f1p6wmx7wku2" />
            Test Embed
          </a>}
      </div>

      {/* Newsletter required banner */}
      {!hasNewsletter && <div className="bg-amber-50 border border-amber-300 rounded-lg p-5 flex items-start gap-4" data-id="element-4191" data-mp="5a2q8b241tjx">
          <div className="flex-shrink-0 p-2 bg-amber-100 rounded-full" data-id="element-4192" data-mp="a95kd0gjyfd4">
            <AlertTriangle className="h-5 w-5 text-amber-600" data-id="element-4193" data-mp="3sfag3y00pmgd" />
          </div>
          <div className="flex-1" data-id="element-4194" data-mp="19ipyym8q9tk">
            <h3 className="text-sm font-semibold text-amber-900" data-id="element-4195" data-mp="1ak2z8mth6dx">
              Newsletter required before embedding
            </h3>
            <p className="mt-1 text-sm text-amber-800" data-id="element-4196" data-mp="3ijgrhg90y2i8">
              You need to create a Newsletter linked to this form before you can
              copy embed codes. The Newsletter sets up the full double opt-in
              flow including email template, mailing list, and confirmation
              handling.
            </p>
          </div>
          <Link to="/newsletters/new" className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 whitespace-nowrap" data-id="element-4197" data-mp="366v7igsobxjf">
            <Plus className="h-4 w-4" data-id="element-4198" data-mp="3fxzmhgicnie9" />
            Create Newsletter
          </Link>
        </div>}

      {/* Missing confirmation_link placeholder warning */}
      {hasNewsletter && missingConfirmPlaceholder && <div className="bg-amber-50 border border-amber-300 rounded-lg p-5 flex items-start gap-4" data-id="element-4199" data-mp="3ozz5ufgu6ykd">
          <div className="flex-shrink-0 p-2 bg-amber-100 rounded-full" data-id="element-4200" data-mp="3kydiu1al93wg">
            <AlertTriangle className="h-5 w-5 text-amber-600" data-id="element-4201" data-mp="2pcy44x96igw" />
          </div>
          <div className="flex-1" data-id="element-4202" data-mp="3twpguodsb3yx">
            <h3 className="text-sm font-semibold text-amber-900" data-id="element-4203" data-mp="3txs7qwb3x6eu">
              Email template is missing{' '}
              <code className="bg-amber-100 px-1 rounded font-mono" data-id="element-4204" data-mp="24k5cnha4l76z">
                {'{{confirmation_link}}'}
              </code>
            </h3>
            <p className="mt-1 text-sm text-amber-800" data-id="element-4205" data-mp="210wobrj84hxy">
              Your email template's confirmation button doesn't use the{' '}
              <code className="bg-amber-100 px-1 rounded font-mono" data-id="element-4206" data-mp="25xddmp25t2cm">
                {'{{confirmation_link}}'}
              </code>{' '}
              placeholder. The embed code will automatically fix this by
              replacing your button's href with the correct confirmation URL —
              but for best results, edit your email template and set the button
              link to{' '}
              <code className="bg-amber-100 px-1 rounded font-mono" data-id="element-4207" data-mp="1n98ha2nu0jnu">
                {'{{confirmation_link}}'}
              </code>
              .
            </p>
          </div>
        </div>}

      <div className="grid gap-6" data-id="element-4208" data-mp="1x034b98wjnac">
        {/* WordPress / Hosted Iframe — LIVE */}
        <div className={`bg-white border-2 border-green-300 rounded-lg p-6 ring-1 ring-green-100 ${!hasNewsletter ? 'opacity-60' : ''}`} data-id="element-4209" data-mp="zics7darg2ka">
          <div className="flex items-center justify-between mb-4" data-id="element-4210" data-mp="3hs8l8l2r5jow">
            <div data-id="element-4211" data-mp="3hqebm2wxle04">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-4212" data-mp="3dqx5r8dleeuf">
                WordPress Embed
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-4213" data-mp="38raqkte7eoiz">
                  ✓ Live &amp; Always Up-to-Date
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-4214" data-mp="2ytwaww03u3rq">
                  Recommended
                </span>
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-4215" data-mp="2q0j8n3g5hpsz">
                <strong className="text-green-700" data-id="element-4216" data-mp="2pyyjjtcluz2i">
                  Paste once, never update again.
                </strong>{' '}
                This iframe loads your form live from the server every time a
                visitor opens the page. Any changes you make to the form design,
                form configuration, email templates, or newsletter settings are
                reflected automatically — no need to re-copy or re-paste.
              </p>
              <p className="text-xs text-gray-400 mt-1.5" data-id="element-4217" data-mp="26h3yitsv598k">
                Paste into a <strong data-id="element-4218" data-mp="1sgjghzurvhg4">Custom HTML block</strong> in Gutenberg or
                any HTML widget.
              </p>
            </div>
            <button onClick={() => handleCopy('wp', wpCode, wpPreRef)} disabled={!wpCode || !hasNewsletter} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0" data-id="element-4219" data-mp="1whv4y21wtwmq">
              {copied === 'wp' ? <Check className="h-4 w-4" data-id="element-4220" data-mp="2fe4h2jt94d43" /> : <Copy className="h-4 w-4" data-id="element-4221" data-mp="33506596bou7n" />}
              {copied === 'wp' ? 'Copied!' : 'Copy Code'}
            </button>
          </div>
          <div ref={wpContainerRef} className="bg-gray-900 rounded-md p-4 overflow-x-auto" style={!hasNewsletter ? {
          userSelect: 'none',
          WebkitUserSelect: 'none'
        } : undefined} data-id="element-4222" data-mp="whjdl7esfa62">
            <pre ref={wpPreRef} className={`text-sm font-mono whitespace-pre-wrap break-all ${!hasNewsletter ? 'text-gray-500 blur-[2px]' : 'text-gray-100'}`} data-id="element-4223" data-mp="119ax4xzj0fmp">
              {wpCode || 'Save the form with a URL slug to generate this code.'}
            </pre>
          </div>
        </div>

        {/* Static embed notice */}
        <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg" data-id="element-4224" data-mp="ii1he6z5wumo">
          <div className="flex-shrink-0 p-1.5 bg-gray-200 rounded-full" data-id="element-4225" data-mp="1yvi94sy5ta93">
            <AlertTriangle className="h-3.5 w-3.5 text-gray-500" data-id="element-4226" data-mp="1yxuqb3w3fgl3" />
          </div>
          <p className="text-xs text-gray-500" data-id="element-4227" data-mp="1tz1a33pf9bh8">
            <strong className="text-gray-700" data-id="element-4228" data-mp="3ngfcc0a33gxf">Static embeds below</strong> — The
            following two options bake all form data into the embed code at copy
            time. If you change the form, email template, or newsletter
            settings, you'll need to re-copy and re-paste the code. For most use
            cases, the WordPress Embed above is the better choice.
          </p>
        </div>

        {/* Standard Iframe Embed — STATIC */}
        <div className={`bg-white border border-gray-200 rounded-lg p-6 ${!hasNewsletter ? 'opacity-60' : ''}`} data-id="element-4229" data-mp="b0bpu8rynf1i">
          <div className="flex items-center justify-between mb-4" data-id="element-4230" data-mp="jyfcj8eomhuw">
            <div data-id="element-4231" data-mp="jwul3rnnzf19">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-4232" data-mp="oy0elrp5f6bu">
                Iframe Embed
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500 uppercase tracking-wide" data-id="element-4233" data-mp="2lqtv89p1vqcd">
                  Static Snapshot
                </span>
              </h3>
              <p className="text-sm text-gray-500" data-id="element-4234" data-mp="2j7f7a4hjvp0z">
                Fully isolated in an iframe with built-in submission handling.
                Works on most platforms except WordPress.{' '}
                <span className="text-amber-600 font-medium" data-id="element-4235" data-mp="29gklt2c4otgl">
                  Must re-copy after changes.
                </span>
              </p>
            </div>
            <button onClick={() => handleCopy('iframe', iframeCode, iframePreRef)} disabled={!iframeCode || !hasNewsletter} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4236" data-mp="3259pn94kjcw1">
              {copied === 'iframe' ? <Check className="h-4 w-4" data-id="element-4237" data-mp="1gsgcm6ucly95" /> : <Copy className="h-4 w-4" data-id="element-4238" data-mp="1bsk9e9th6aun" />}
              {copied === 'iframe' ? 'Copied!' : 'Copy Code'}
            </button>
          </div>
          <div ref={iframeContainerRef} className="bg-gray-900 rounded-md p-4 overflow-x-auto max-h-48 overflow-y-auto" style={!hasNewsletter ? {
          userSelect: 'none',
          WebkitUserSelect: 'none'
        } : undefined} data-id="element-4239" data-mp="a15zn1jxumtc">
            <pre ref={iframePreRef} className={`text-sm font-mono whitespace-pre-wrap break-all ${!hasNewsletter ? 'text-gray-500 blur-[2px]' : 'text-gray-100'}`} data-id="element-4240" data-mp="sq4c3fj477cx">
              {iframeCode || 'No HTML content — add form HTML to generate embed code.'}
            </pre>
          </div>
        </div>

        {/* JavaScript Embed — STATIC */}
        <div className={`bg-white border border-gray-200 rounded-lg p-6 ${!hasNewsletter ? 'opacity-60' : ''}`} data-id="element-4241" data-mp="34hq2oohg7ia4">
          <div className="flex items-center justify-between mb-4" data-id="element-4242" data-mp="2zjg5678945lc">
            <div data-id="element-4243" data-mp="1f0m776sek79u">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-4244" data-mp="1f02xgdcwop6w">
                JavaScript Embed
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500 uppercase tracking-wide" data-id="element-4245" data-mp="1k0qr76sirk4t">
                  Static Snapshot
                </span>
              </h3>
              <p className="text-sm text-gray-500" data-id="element-4246" data-mp="w8jychm05va5">
                Injects the form directly into your page with built-in
                submission handling. Does not work on WordPress.{' '}
                <span className="text-amber-600 font-medium" data-id="element-4247" data-mp="mb5junjn3gkh">
                  Must re-copy after changes.
                </span>
              </p>
            </div>
            <button onClick={() => handleCopy('script', scriptCode, scriptPreRef)} disabled={!scriptCode || !hasNewsletter} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4248" data-mp="rc2ywr9cruhr">
              {copied === 'script' ? <Check className="h-4 w-4" data-id="element-4249" data-mp="2a8og16gqovy9" /> : <Copy className="h-4 w-4" data-id="element-4250" data-mp="2a96hchi9e0d1" />}
              {copied === 'script' ? 'Copied!' : 'Copy Code'}
            </button>
          </div>
          <div ref={scriptContainerRef} className="bg-gray-900 rounded-md p-4 overflow-x-auto max-h-48 overflow-y-auto" style={!hasNewsletter ? {
          userSelect: 'none',
          WebkitUserSelect: 'none'
        } : undefined} data-id="element-4251" data-mp="32xvanud2p1e8">
            <pre ref={scriptPreRef} className={`text-sm font-mono whitespace-pre-wrap break-all ${!hasNewsletter ? 'text-gray-500 blur-[2px]' : 'text-gray-100'}`} data-id="element-4252" data-mp="3lntjicop2yix">
              {scriptCode || 'No HTML content — add form HTML to generate embed code.'}
            </pre>
          </div>
        </div>
      </div>

      {/* Platform Instructions */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mt-8" data-id="element-4253" data-mp="211fhqprjrj6">
        <div className="border-b border-gray-200" data-id="element-4254" data-mp="23e42mq11057">
          <nav className="flex -mb-px" aria-label="Tabs" data-id="element-4255" data-mp="715tna378a68">
            <button onClick={() => setActiveInstructionTab('wordpress')} className={`w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm ${activeInstructionTab === 'wordpress' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4256" data-mp="bz7e9zg9whjt">
              WordPress
            </button>
            <button onClick={() => setActiveInstructionTab('html')} className={`w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm ${activeInstructionTab === 'html' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4257" data-mp="psgizmta0ew0">
              Plain HTML
            </button>
            <button onClick={() => setActiveInstructionTab('react')} className={`w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm ${activeInstructionTab === 'react' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4258" data-mp="1rgtz3dehx8ax">
              React / Next.js
            </button>
          </nav>
        </div>
        <div className="p-6" data-id="element-4259" data-mp="18kccfaei7ylj">
          {activeInstructionTab === 'wordpress' && <div className="space-y-4 text-sm text-gray-600" data-id="element-4260" data-mp="1mjbycaamtze0">
              <h4 className="font-medium text-gray-900 text-base" data-id="element-4261" data-mp="1ig5tn7364g9n">
                How to embed in WordPress
              </h4>
              <ol className="list-decimal list-inside space-y-3" data-id="element-4262" data-mp="2xvl67yu7ppde">
                <li data-id="element-4263" data-mp="2twccgtz69rhu">
                  Copy the <strong data-id="element-4264" data-mp="2tv17iqtzqdp5">WordPress Embed</strong> code from the green
                  section above.
                </li>
                <li data-id="element-4265" data-mp="31h6e266isx1l">
                  In your WordPress editor (Gutenberg), click the{' '}
                  <strong data-id="element-4266" data-mp="36c3lx9s2d0cw">+</strong> button to add a new block.
                </li>
                <li data-id="element-4267" data-mp="2rigy4iy6pmnz">
                  Search for and select the <strong data-id="element-4268" data-mp="2wfqsmd0fkoio">Custom HTML</strong> block.
                </li>
                <li data-id="element-4269" data-mp="2whtv3wirsa4a">Paste the copied code into the HTML block.</li>
                <li data-id="element-4270" data-mp="3k6wd55hbg4zb">
                  Click <strong data-id="element-4271" data-mp="3k7o4u57slikb">Preview</strong> to see your form, then{' '}
                  <strong data-id="element-4272" data-mp="3p3d0hxgoyfbc">Update</strong> or <strong data-id="element-4273" data-mp="3p6qyyum8i4di">Publish</strong> your page.
                </li>
              </ol>
              <div className="bg-blue-50 p-4 rounded-md mt-4" data-id="element-4274" data-mp="3afi5ubj4i0vm">
                <p className="text-blue-800" data-id="element-4275" data-mp="3fdteiixom5c9">
                  <strong data-id="element-4276" data-mp="3fciaci0bqvkm">Note:</strong> The WordPress embed uses an iframe that
                  automatically loads the latest version of your form. You never
                  need to update this code even if you change the form design
                  later.
                </p>
              </div>
            </div>}
          {activeInstructionTab === 'html' && <div className="space-y-4 text-sm text-gray-600" data-id="element-4277" data-mp="gngmcpavg39u">
              <h4 className="font-medium text-gray-900 text-base" data-id="element-4278" data-mp="ckaifob8eo6j">
                How to embed in plain HTML
              </h4>
              <ol className="list-decimal list-inside space-y-3" data-id="element-4279" data-mp="7lh3drgaqp49">
                <li data-id="element-4280" data-mp="2n5ubixtsin3">
                  Choose either the <strong data-id="element-4281" data-mp="2o783v687j8i">Iframe Embed</strong> or{' '}
                  <strong data-id="element-4282" data-mp="3u1blxp2mpe2d">JavaScript Embed</strong> code above.
                </li>
                <li data-id="element-4283" data-mp="3p27ccxbslebi">Open your HTML file in your code editor.</li>
                <li data-id="element-4284" data-mp="3p0e5bgx9os2z">
                  Locate the section where you want the form to appear (e.g.,
                  inside a{' '}
                  <code className="bg-gray-100 px-1 rounded" data-id="element-4285" data-mp="3g1j0a85ylo19">&lt;div&gt;</code>{' '}
                  or{' '}
                  <code className="bg-gray-100 px-1 rounded" data-id="element-4286" data-mp="3b262isdjuwjw">
                    &lt;section&gt;
                  </code>
                  ).
                </li>
                <li data-id="element-4287" data-mp="1qmrkewz78bav">Paste the copied code directly into your HTML.</li>
                <li data-id="element-4288" data-mp="1vi6s176m5ayu">Save the file and refresh your browser.</li>
              </ol>
              <div className="bg-amber-50 p-4 rounded-md mt-4" data-id="element-4289" data-mp="20jw7cz0vcasq">
                <p className="text-amber-800" data-id="element-4290" data-mp="24k6am3xa0n3w">
                  <strong data-id="element-4291" data-mp="24j4w1ph1xihf">Important:</strong> These are static snapshots. If you
                  update your form's design or settings in the builder, you must
                  come back here, copy the new code, and replace it on your
                  website.
                </p>
              </div>
            </div>}
          {activeInstructionTab === 'react' && <div className="space-y-4 text-sm text-gray-600" data-id="element-4292" data-mp="1lmlwnrw1v8y2">
              <h4 className="font-medium text-gray-900 text-base" data-id="element-4293" data-mp="p3ny6eqxm183">
                How to embed in React or Next.js
              </h4>
              <p data-id="element-4294" data-mp="t47vbibi94u8">
                The easiest way to embed the form in a React application is to
                use an iframe pointing to the live form URL.
              </p>
              <div className="bg-gray-900 rounded-md p-4 overflow-x-auto mt-2" data-id="element-4295" data-mp="3doi3xx7w7ewh">
                <pre className="text-gray-100 font-mono text-sm" data-id="element-4296" data-mp="3sliuhxecl3td">
                  {`export default function SignupForm() {
  return (
    <div className="w-full max-w-md mx-auto">
      <iframe 
        src="${window.location.origin}/embed/${formSlug || 'your-form-slug'}" 
        width="100%" 
        height="600" 
        frameBorder="0" 
        style={{ border: 'none', overflow: 'hidden' }}
        allowTransparency={true}
      />
    </div>
  );
}`}
                </pre>
              </div>
              <p className="mt-4" data-id="element-4297" data-mp="a7jbayqdyi6e">
                This approach ensures your form is always up-to-date without
                needing to redeploy your application when form settings change.
              </p>
            </div>}
        </div>
      </div>
    </div>;
}